import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/shared/EmptyState'
import { SearchEmptyState } from '@/components/shared/SearchEmptyState'
import { SearchBar } from '@/components/shared/SearchBar'
import { MoveCard } from '@/components/moves/MoveCard'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ErrorState } from '@/components/shared/ErrorState'
import { useMoves } from '@/hooks/useMoves'
import { useListQuery } from '@/hooks/useListQuery'

export function MoveArchivePage() {
  const navigate = useNavigate()
  const listQuery = useListQuery(20, 300)

  // Fetch moves with search and pagination
  const { data, isLoading, isError, error, refetch } = useMoves(
    listQuery.filters
  )

  const handleCreateMove = () => {
    navigate('/moves/new')
  }

  // Determine states
  const isEmpty =
    !isLoading && data?.moves.length === 0 && !listQuery.searchText
  const isSearchEmpty =
    !isLoading && data?.moves.length === 0 && listQuery.searchText.length > 0

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {/* Search Bar + Create Button */}
        {!isEmpty && (
          <div className="mb-6 flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                value={listQuery.searchText}
                onChange={listQuery.setSearchText}
                placeholder="Search moves..."
                ariaLabel="Search moves"
                className="max-w-md"
              />
            </div>
            <Button
              onClick={handleCreateMove}
              size="sm"
              className="bg-[#0033A0] hover:bg-[#002080]"
              aria-label="Create new move"
            >
              + Create Move
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="flex min-h-[400px] items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          <ErrorState
            error={error}
            onRetry={refetch}
            message="Failed to load moves"
          />
        ) : isEmpty ? (
          <EmptyState
            icon="🥋"
            title="No moves yet"
            description="Start building your BJJ technique library by creating your first move. Add videos, descriptions, and organize them into series."
            actionLabel="Create First Move"
            onAction={handleCreateMove}
          />
        ) : isSearchEmpty ? (
          <SearchEmptyState
            searchText={listQuery.searchText}
            onClearSearch={listQuery.clearSearch}
            resourceType="moves"
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data?.moves.map(move => (
              <MoveCard key={move.id} move={move} />
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
